import React, { useEffect, useState } from 'react';
import invariant from 'tiny-invariant';
import { Dialog } from 'components/Dialog';
import { Button, Divider, Stack, Typography } from '@mui/material';
import { Add, DeleteOutlined, EditTwoTone } from '@mui/icons-material';
import { WebsiteUrlRow } from './WebsiteUrlRow';
import { WebsiteEditor } from './WebsiteEditor';
import { Website } from 'types';
import { useCampaignForm } from 'hooks';
import { focusWebsiteUrl } from 'shared';

export type WebsiteActionType = 'DELETE';

type WebsiteDialogAction = {
  title: string;
  content: React.ReactNode;
  submitTitle: string;
  onSubmit: () => void;
  onCancel?: () => void;
};

type WebsiteAction = {
  type: WebsiteActionType;
  website: Website;
  index: number;
};

export const WebsiteList = () => {
  const { isInitialized, websites, addNewWebsite, removeWebsite, setWebsiteEditing } = useCampaignForm();
  const [websiteAction, setWebsiteAction] = useState<WebsiteAction | null>(null);
  const [hasWebsitesInitially, setHasWebsitesInitially] = useState<boolean | null>(null);

  const dialogConfig: Record<WebsiteActionType, WebsiteDialogAction> = {
    DELETE: {
      title: 'Bist du sicher, dass du den Webseiten-Link löschen willst?',
      content: (
        <Typography>
          Die bereits hochgeladenen Creatives werden dadurch ebenfalls entfernt und müssen erneut hochgeladen werden.
        </Typography>
      ),
      submitTitle: 'Fortfahren',
      onSubmit: async () => {
        invariant(websiteAction, 'no active action');
        await removeWebsite(websiteAction.index);
        setWebsiteAction(null);
      },
    },
  };

  const handleAddClick = () => {
    const count = websites.length;
    if (websites.at(count - 1)?.url === '') {
      focusWebsiteUrl(count - 1);
    } else {
      addNewWebsite();
    }
  };

  const handleEditClick = (index: number) => {
    setWebsiteEditing(index, true);
    focusWebsiteUrl(index);
    setWebsiteAction(null);
  };

  // check if there is any website with a url on mount (fired once on fetch),
  // or on the last website delete
  useEffect(() => {
    if (hasWebsitesInitially === null && isInitialized && websites) {
      setHasWebsitesInitially(websites.some((item) => !!item.url));
    } else if (hasWebsitesInitially && websites.length === 0) {
      setHasWebsitesInitially(false);
    }
  }, [hasWebsitesInitially, isInitialized, websites]);

  return (
    <>
      {websites.map((website, index) => (
        <Stack key={website.id} width={1}>
          {!hasWebsitesInitially || website.isEditing || website.url === '' ? (
            <WebsiteEditor index={index} hasWebsitesInitially={!!hasWebsitesInitially} />
          ) : (
            <>
              <WebsiteUrlRow url={website.url} />
              <Stack flexDirection="row" gap={2} mt={2}>
                <Button size="small" variant="text" startIcon={<EditTwoTone />} onClick={() => handleEditClick(index)}>
                  Anpassen
                </Button>
                <Button
                  size="small"
                  variant="text"
                  startIcon={<DeleteOutlined />}
                  onClick={() => setWebsiteAction({ type: 'DELETE', website, index })}
                >
                  Löschen
                </Button>
              </Stack>
              <Divider sx={{ mb: 4, mt: 4 }} />
            </>
          )}
        </Stack>
      ))}

      <Button variant="text" startIcon={<Add />} onClick={handleAddClick} sx={{ px: 1, py: 1.5 }}>
        WEITERE WEBSEITE HINZUFÜGEN
      </Button>

      <Dialog
        open={!!websiteAction}
        onPrimaryActionClick={websiteAction ? dialogConfig[websiteAction.type].onSubmit : undefined}
        onSecondaryActionClick={
          websiteAction ? (dialogConfig[websiteAction.type].onCancel ?? (() => setWebsiteAction(null))) : undefined
        }
        disableEnforceFocus
        disableRestoreFocus
        title={(websiteAction && dialogConfig[websiteAction.type].title) ?? null}
        primaryActionTitle={websiteAction ? dialogConfig[websiteAction.type].submitTitle : undefined}
        content={websiteAction ? dialogConfig[websiteAction.type].content : undefined}
      />
    </>
  );
};
